import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/utils/i18n';
import { Shield, TrendingUp, Brain, MessageSquare, Target, ChevronRight, Sparkles, Mail, Lock, User, AlertTriangle, Camera, BookOpen, Zap, Trophy, BarChart2, CheckCircle, Flame, Star, Gem } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BACKEND_URL_VIP = process.env.REACT_APP_BACKEND_URL;
const API_VIP = `${BACKEND_URL_VIP}/api`;

// VIP Code Validator mini-component
function VipCodeValidator({ code, language }) {
  const [status, setStatus] = React.useState(null);
  React.useEffect(() => {
    const timer = setTimeout(async () => {
      if (code.length < 4) return;
      try {
        const res = await fetch(`${API_VIP}/vip/validate/${code}`);
        const data = await res.json();
        setStatus(data);
      } catch { setStatus(null); }
    }, 500);
    return () => clearTimeout(timer);
  }, [code]);

  if (!status) return null;
  return (
    <div className={`mt-1.5 px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-2 ${status.valid ? 'bg-green-500/15 text-green-400 border border-green-500/30' : 'bg-red-500/15 text-red-400 border border-red-500/30'}`}>
      {status.valid ? '✓' : '✗'} {status.message}
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, token } = useAuth();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  const [articles, setArticles] = useState([]);
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [pendingPlan, setPendingPlan] = useState(null);

  useEffect(() => {
    if (token) {
      // If coming from pricing with a plan, go to checkout
      const plan = searchParams.get('plan');
      if (plan) {
        navigate(`/checkout?plan=${plan}`);
      } else {
        navigate('/dashboard');
      }
    } else {
      fetchArticles();
      // Auto-open signup if redirected from pricing
      const mode = searchParams.get('mode');
      const plan = searchParams.get('plan');
      if (mode === 'register') {
        setAuthMode('register');
        setShowAuth(true);
        if (plan) setPendingPlan(plan);
      }
    }
  }, [token]);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles?lang=${language}`);
      setArticles(response.data.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
  };
  
  useEffect(() => {
    if (!token) {
      fetchArticles();
    }
  }, [language]);

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      login(response.data.access_token, response.data.user);
      toast.success(authMode === 'login' ? (language === 'fr' ? 'Connecté !' : 'Connected!') : (language === 'fr' ? 'Compte créé !' : 'Account created!'));
      // Redirect to checkout if coming from pricing
      if (pendingPlan) {
        navigate(`/checkout?plan=${pendingPlan}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication error');
    }
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'Crypto': { color: 'bg-orange-500', text: 'CRYPTO' },
      'Actions': { color: 'bg-blue-500', text: 'TECH' },
      'Stratégie': { color: 'bg-green-500', text: 'ENERGY' },
      'Analyse': { color: 'bg-purple-500', text: 'ANALYSIS' }
    };
    return badges[category] || { color: 'bg-red-500', text: category.toUpperCase() };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0a] via-[#1a0a0a] to-[#0a0a0a]">
      {/* Header */}
      <header className="border-b border-orange-900/20 backdrop-blur-sm bg-black/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo.png" alt="DO NOT BULL" className="w-10 h-10" />
            <div>
              <div className="text-2xl font-bold text-white tracking-tight">DO NOT BULL</div>
              <div className="text-[10px] text-orange-500 font-semibold tracking-widest">ANTI-SPECULATION AI v2.0</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/pricing')}
              variant="ghost"
              className="text-gray-400 hover:text-orange-500 font-semibold hidden sm:flex"
            >
              {t('pricing')}
            </Button>
            <a
              href="https://play.google.com/store/apps/details?id=com.donotbull.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 bg-black border border-gray-700 hover:border-orange-500 text-white text-xs font-semibold px-3 py-2 rounded-full transition-all"
            >
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current text-green-400" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-11.57L13.34 9l-10.16 14.76zm-.96-21.5C2.08 2.57 2 2.9 2 3.24v17.52c0 .34.08.67.22.96L13.06 12 2.22 2.26zM20.49 10.34l-2.76-1.57-3.67 3.36 3.67 3.36 2.8-1.59c.8-.45.8-1.01-.04-1.56zM4.17.24L16.77 11.8 13.34 15 4.17.24z" />
              </svg>
              Google Play
            </a>
            <a
              href="https://apps.apple.com/app/id6774533419"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-1.5 bg-black border border-gray-700 hover:border-orange-500 text-white text-xs font-semibold px-3 py-2 rounded-full transition-all"
            >
              <svg viewBox="0 0 814 1000" className="w-3.5 h-3.5 fill-current text-white" xmlns="http://www.w3.org/2000/svg">
                <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 405.8 0 263.6 0 127.3c0-150.1 97.8-248.4 250.2-248.4 69 0 127.4 36.2 179 81.9 13.4 11.5 27.5 23.4 42.5 35.6 44.6-37.3 100.3-108.7 179.5-108.7 3.2 0 6.4.3 9.6.5 2.2.1 4.4.3 6.5.4l.2.1z"/>
              </svg>
              App Store
            </a>
            <LanguageSwitcher />
            <Button
              data-testid="login-button"
              onClick={() => { setAuthMode('login'); setShowAuth(true); }}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:text-white hover:border-orange-500/50 font-semibold px-5 rounded-full hidden sm:flex"
            >
              {language === 'fr' ? 'Connexion' : language === 'es' ? 'Iniciar sesión' : 'Log in'}
            </Button>
            <Button 
              data-testid="try-it-button"
              onClick={() => { setAuthMode('register'); setShowAuth(true); }}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-6 rounded-full"
            >
              {t('tryIt')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-16 text-center">
        <div className="inline-block px-4 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-semibold mb-6 animate-pulse">
          {t('stopFomo')}
        </div>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
          <span className="text-white">{t('heroTitle')}</span>
          <br />
          <span className="bg-gradient-to-r from-orange-500 via-red-500 to-orange-600 bg-clip-text text-transparent">{t('heroTitleBold')}</span>
        </h1>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
          {t('heroSubtitle')}
        </p>

        {/* Hero CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            data-testid="start-free-hero-btn"
            onClick={() => { setAuthMode('register'); setShowAuth(true); }}
            className="bg-green-500 hover:bg-green-600 text-black font-bold px-8 py-4 text-base rounded-full"
          >
            {language === 'fr' ? 'Commencer gratuitement' : language === 'es' ? 'Empezar gratis' : 'Start for free'} →
          </Button>
          <Button
            data-testid="try-it-button"
            onClick={() => navigate('/pricing')}
            variant="outline"
            className="border-orange-500/40 text-orange-400 hover:bg-orange-500/10 px-8 py-4 text-base rounded-full"
          >
            {language === 'fr' ? 'Voir les plans' : language === 'es' ? 'Ver planes' : 'View plans'} — €19/
            {language === 'fr' ? 'mois' : language === 'es' ? 'mes' : 'mo'}
          </Button>
        </div>
        <p className="text-gray-600 text-sm mt-4">
          {language === 'fr' ? 'Gratuit pour toujours • Pas de carte bancaire requise' : language === 'es' ? 'Gratis para siempre • Sin tarjeta de crédito' : 'Free forever • No credit card required'}
        </p>
      </section>

      {/* Educational Purpose Disclaimer */}
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-gradient-to-br from-orange-500/10 via-red-500/10 to-orange-500/10 rounded-2xl p-8 border border-orange-500/30">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-500" />
              <h3 className="text-xl font-bold text-white">{t('educationalPurpose')}</h3>
            </div>
            <p className="text-sm text-gray-300 mb-2">
              <strong>{t('educationalText')}</strong>
            </p>
            <button 
              onClick={() => navigate('/disclaimer')}
              className="text-orange-500 hover:text-orange-400 text-sm font-semibold underline"
            >
              {t('readFullDisclaimer')}
            </button>
          </div>
        </div>
      </section>

      {/* Today's News */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-white">{t('todaysAnalysis')}</h2>
          <div className="text-sm text-gray-500">{t('updatedDaily')}</div>
        </div>

        <div data-testid="news-grid" className="grid md:grid-cols-3 gap-6">
          {articles.map((article, idx) => {
            const badge = getCategoryBadge(article.category);
            return (
              <article 
                key={article.id}
                data-testid={`news-card-${idx}`}
                onClick={() => navigate(`/article/${article.id}`)}
                className="group relative bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl overflow-hidden border border-zinc-800 hover:border-orange-500/50 transition-all cursor-pointer"
              >
                <div className="absolute top-4 left-4 z-10">
                  <span className={`${badge.color} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                    {badge.text}
                  </span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                <div className="relative p-6 pt-16 min-h-[400px] flex flex-col justify-end">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-orange-400 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                    {article.summary}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{article.author}</span>
                    <ChevronRight className="w-4 h-4 text-orange-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
                <div className="absolute inset-0 bg-orange-500/0 group-hover:bg-orange-500/5 transition-all"></div>
              </article>
            );
          })}
        </div>
      </section>

      {/* ============ EVERYTHING INVESTORS NEED ============ */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm font-bold uppercase tracking-widest mb-4">
            <Sparkles className="w-4 h-4" />
            {language === 'en' ? 'The Full Platform' : language === 'es' ? 'La Plataforma Completa' : 'La Plateforme Complète'}
          </div>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            {language === 'en' ? 'Everything investors need' : language === 'es' ? 'Todo lo que los inversores necesitan' : 'Tout ce dont les investisseurs ont besoin'}
            <br />
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
              {language === 'en' ? 'in one platform' : language === 'es' ? 'en una plataforma' : 'en une seule plateforme'}
            </span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {language === 'en'
              ? 'From portfolio analysis to professional-grade research — all powered by AI.'
              : language === 'es'
              ? 'Desde análisis de cartera hasta investigación profesional, todo impulsado por IA.'
              : 'De l\'analyse de portfolio à la recherche institutionnelle — tout propulsé par IA.'}
          </p>
        </div>

        <div data-testid="services-grid" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: <Target className="w-6 h-6" />,
              color: 'text-orange-400 bg-orange-500/10',
              badge: 'Premium',
              badgeColor: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
              title: language === 'en' ? 'AI Portfolio Analyzer' : language === 'es' ? 'Analizador de Cartera IA' : 'Analyseur de Portfolio IA',
              desc: language === 'en' ? 'AI risk scoring, health analysis and personalized improvement suggestions.' : language === 'es' ? 'Puntuación de riesgo IA y sugerencias de mejora.' : 'Score de risque IA, santé du portfolio et suggestions personnalisées.',
              testid: 'service-portfolio'
            },
            {
              icon: <Shield className="w-6 h-6" />,
              color: 'text-blue-400 bg-blue-500/10',
              badge: language === 'en' ? 'Free' : language === 'es' ? 'Gratis' : 'Gratuit',
              badgeColor: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
              title: language === 'en' ? 'Market News Feed' : language === 'es' ? 'Feed de Noticias' : 'Fil d\'Actualités',
              desc: language === 'en' ? 'Real-time financial news curated and filtered by AI.' : language === 'es' ? 'Noticias financieras en tiempo real curadas por IA.' : 'Actualités financières en temps réel filtrées par IA.',
              testid: 'service-news'
            },
            {
              icon: <Gem className="w-6 h-6" />,
              color: 'text-yellow-400 bg-yellow-500/10',
              badge: 'Premium',
              badgeColor: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
              title: language === 'en' ? 'AI Gems' : language === 'es' ? 'Gemas IA' : 'Pépites IA',
              desc: language === 'en' ? 'AI-detected investment opportunities across stocks and crypto with scores and signals.' : language === 'es' ? 'Oportunidades de inversión detectadas por IA.' : 'Opportunités d\'investissement détectées par IA avec scores et signaux.',
              testid: 'service-gems'
            },
            {
              icon: <BookOpen className="w-6 h-6" />,
              color: 'text-green-400 bg-green-500/10',
              badge: language === 'en' ? 'Free' : language === 'es' ? 'Gratis' : 'Gratuit',
              badgeColor: 'bg-green-500/20 text-green-400 border-green-500/30',
              title: language === 'en' ? 'Investment Academy' : language === 'es' ? 'Academia de Inversión' : 'Académie d\'Investissement',
              desc: language === 'en' ? '4 structured modules, quizzes, XP system and daily lessons from beginner to advanced.' : language === 'es' ? '4 módulos estructurados desde principiante hasta avanzado.' : '4 modules structurés, quiz, système XP et leçons quotidiennes.',
              testid: 'service-academy'
            },
            {
              icon: <BarChart2 className="w-6 h-6" />,
              color: 'text-cyan-400 bg-cyan-500/10',
              badge: language === 'en' ? 'Free' : language === 'es' ? 'Gratis' : 'Gratuit',
              badgeColor: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
              title: language === 'en' ? 'Trading Simulator' : language === 'es' ? 'Simulador de Trading' : 'Simulateur de Trading',
              desc: language === 'en' ? 'Practice with $10,000 virtual portfolio. Real-time prices, zero real risk.' : language === 'es' ? 'Practica con $10,000 virtual y precios en tiempo real.' : 'Pratiquez avec 10 000$ virtuels. Prix réels, zéro risque.',
              testid: 'service-simulator'
            },
            {
              icon: <Brain className="w-6 h-6" />,
              color: 'text-purple-400 bg-purple-500/10',
              badge: 'Pro',
              badgeColor: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
              title: language === 'en' ? 'Professional Analysis' : language === 'es' ? 'Análisis Profesional' : 'Analyse Professionnelle',
              desc: language === 'en' ? 'Goldman Sachs, BlackRock, McKinsey frameworks. Institutional-grade AI reports.' : language === 'es' ? 'Frameworks de Goldman Sachs, BlackRock y McKinsey.' : 'Frameworks Goldman Sachs, BlackRock, McKinsey. Rapports IA de niveau institutionnel.',
              testid: 'service-pro'
            },
            {
              icon: <MessageSquare className="w-6 h-6" />,
              color: 'text-pink-400 bg-pink-500/10',
              badge: 'Premium',
              badgeColor: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
              title: language === 'en' ? 'AI Investment Chat' : language === 'es' ? 'Chat de Inversión IA' : 'Chat Investissement IA',
              desc: language === 'en' ? 'Ask anything about markets, stocks and crypto. Personalized to your portfolio.' : language === 'es' ? 'Pregunta sobre mercados, acciones y cripto.' : 'Posez vos questions sur les marchés. Personnalisé selon votre portfolio.',
              testid: 'service-chat'
            },
            {
              icon: <Camera className="w-6 h-6" />,
              color: 'text-red-400 bg-red-500/10',
              badge: 'Premium',
              badgeColor: 'bg-red-500/20 text-red-400 border-red-500/30',
              title: language === 'en' ? 'Chart Analyzer' : language === 'es' ? 'Analizador de Gráficos' : 'Analyseur de Graphiques',
              desc: language === 'en' ? 'Upload any chart and get instant AI technical analysis: trend, patterns, signals.' : language === 'es' ? 'Sube un gráfico y obtén análisis técnico instantáneo.' : 'Uploadez un graphique et obtenez une analyse technique IA instantanée.',
              testid: 'service-chart'
            },
          ].map((service, i) => (
            <div
              key={i}
              data-testid={service.testid}
              onClick={() => { setAuthMode('register'); setShowAuth(true); }}
              className="group bg-gradient-to-br from-zinc-900 to-black rounded-2xl p-5 border border-zinc-800 hover:border-orange-500/40 transition-all cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${service.color} group-hover:scale-110 transition-transform`}>
                  {service.icon}
                </div>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${service.badgeColor}`}>
                  {service.badge}
                </span>
              </div>
              <h3 className="font-bold text-white text-sm mb-2 group-hover:text-orange-400 transition-colors">{service.title}</h3>
              <p className="text-gray-400 text-xs leading-relaxed">{service.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Button
            onClick={() => { setAuthMode('register'); setShowAuth(true); }}
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-10 py-5 rounded-full text-base"
          >
            {language === 'en' ? 'Access all tools for free →' : language === 'es' ? 'Acceder a todas las herramientas →' : 'Accéder à tous les outils gratuitement →'}
          </Button>
        </div>
      </section>

      {/* ============ PLATFORM PREVIEW ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
            {language === 'en' ? 'Platform Preview' : language === 'es' ? 'Vista Previa' : 'Aperçu de la Plateforme'}
          </h2>
          <p className="text-gray-400">
            {language === 'en' ? 'A professional investing experience, built for modern investors.' : language === 'es' ? 'Una experiencia de inversión profesional para inversores modernos.' : 'Une expérience d\'investissement professionnelle, pensée pour les investisseurs modernes.'}
          </p>
        </div>

        {/* Dashboard Mock */}
        <div className="relative rounded-3xl overflow-hidden border border-zinc-800 bg-zinc-950">
          {/* Browser bar */}
          <div className="flex items-center gap-2 px-4 py-3 bg-zinc-900 border-b border-zinc-800">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500/60" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
              <div className="w-3 h-3 rounded-full bg-green-500/60" />
            </div>
            <div className="flex-1 mx-4 bg-zinc-800 rounded-lg px-3 py-1 text-xs text-gray-500 text-center">
              donotbull.com/dashboard
            </div>
          </div>

          {/* Mock Dashboard Content */}
          <div className="p-6 bg-[#0a0a0a]">
            {/* Nav mock */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-800">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500 to-red-500" />
                  <span className="text-white font-bold text-sm">Do Not Bull</span>
                </div>
                <div className="hidden sm:flex gap-4 text-xs text-gray-500">
                  {['Dashboard', 'Portfolio', 'Gems', 'Academy', 'Analyse Pro'].map(item => (
                    <span key={item} className={item === 'Dashboard' ? 'text-orange-400 font-semibold' : ''}>{item}</span>
                  ))}
                </div>
              </div>
              <div className="w-6 h-6 rounded-full bg-orange-500/20 border border-orange-500/40" />
            </div>

            {/* Stats row mock */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Portfolio Value', value: '$24,830', color: 'text-white' },
                { label: 'Total P&L', value: '+$2,340', color: 'text-green-400' },
                { label: 'AI Health Score', value: '78/100', color: 'text-orange-400' },
                { label: 'Risk Level', value: 'Medium', color: 'text-blue-400' },
              ].map((stat, i) => (
                <div key={i} className="bg-zinc-900/80 rounded-xl p-3 border border-zinc-800">
                  <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
                  <div className={`font-bold text-sm ${stat.color}`}>{stat.value}</div>
                </div>
              ))}
            </div>

            {/* Main grid mock */}
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {/* Market Sentiment */}
              <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800">
                <div className="text-xs text-gray-400 font-semibold mb-3 flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-orange-500" />
                  AI Market Sentiment
                </div>
                <div className="space-y-2">
                  {[{ label: 'Bullish', pct: 68, color: 'bg-green-400' }, { label: 'Neutral', pct: 18, color: 'bg-yellow-400' }, { label: 'Bearish', pct: 14, color: 'bg-red-400' }].map(m => (
                    <div key={m.label} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-14">{m.label}</span>
                      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full">
                        <div className={`${m.color} h-1.5 rounded-full`} style={{ width: `${m.pct}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 w-8">{m.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Opportunities */}
              <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800">
                <div className="text-xs text-gray-400 font-semibold mb-3 flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  {language === 'en' ? "Today's Opportunities" : language === 'es' ? "Oportunidades de Hoy" : "Opportunités du Jour"}
                </div>
                <div className="space-y-1.5">
                  {[{ sym: 'NVDA', signal: 'Breakout Setup', color: 'text-blue-400 bg-blue-500/10' }, { sym: 'BTC', signal: 'Accumulation Zone', color: 'text-orange-400 bg-orange-500/10' }, { sym: 'PLTR', signal: 'Momentum Signal', color: 'text-green-400 bg-green-500/10' }].map(o => (
                    <div key={o.sym} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-bold ${o.color}`}>{o.sym.substring(0, 2)}</div>
                        <span className="text-xs font-semibold text-white">{o.sym}</span>
                      </div>
                      <span className="text-xs text-green-400">{o.signal}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gems preview */}
            <div className="bg-zinc-900/60 rounded-xl p-4 border border-zinc-800">
              <div className="text-xs text-gray-400 font-semibold mb-3 flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-purple-400" />
                {language === 'en' ? 'AI Gems — Top Signals' : language === 'es' ? 'Gemas IA — Señales Top' : 'Pépites IA — Signaux Top'}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { sym: 'AAPL', score: 91, signal: 'Momentum', change: '+3.2%' },
                  { sym: 'ETH', score: 87, signal: 'DeFi Signal', change: '+5.1%' },
                  { sym: 'MSFT', score: 84, signal: 'Earnings Beat', change: '+2.8%' },
                ].map(g => (
                  <div key={g.sym} className="bg-zinc-800/60 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-black text-white">{g.sym}</span>
                      <span className="text-xs font-bold text-orange-400">{g.score}</span>
                    </div>
                    <div className="text-xs text-gray-500">{g.signal}</div>
                    <div className="text-xs text-green-400 font-semibold mt-1">{g.change}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Shield className="w-5 h-5 text-orange-400" />, title: t('antiSpeculation'), desc: t('antiSpeculationDesc') },
            { icon: <TrendingUp className="w-5 h-5 text-blue-400" />, title: t('cryptoStocks'), desc: t('cryptoStocksDesc') },
            { icon: <Brain className="w-5 h-5 text-purple-400" />, title: t('multiAI'), desc: t('multiAIDesc') },
            { icon: <Target className="w-5 h-5 text-green-400" />, title: t('portfolioFirst'), desc: t('portfolioFirstDesc') },
          ].map((item, i) => (
            <div key={i} className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl p-5 border border-zinc-800">
              <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center mb-3">
                {item.icon}
              </div>
              <h3 className="font-bold text-white text-sm mb-2">{item.title}</h3>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>
      {/* ============ ACADÉMIE SECTION ============ */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        {/* Label */}
        <div className="flex items-center justify-center mb-4">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-sm font-bold tracking-widest uppercase">
            <BookOpen className="w-4 h-4" />
            {t('academyBadge')}
          </span>
        </div>

        <div className="text-center mb-14">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4 leading-tight">
            {t('academyTitle')}<br />
            <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">{t('academyTitleBold')}</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('academySubtitle')}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          <div className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl p-6 border border-zinc-800 hover:border-orange-500/40 transition-all group text-center">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <BookOpen className="w-6 h-6 text-orange-500" />
            </div>
            <h3 className="font-bold text-white mb-2">{t('academyFeature1Title')}</h3>
            <p className="text-gray-400 text-sm">{t('academyFeature1Desc')}</p>
          </div>
          <div className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl p-6 border border-zinc-800 hover:border-orange-500/40 transition-all group text-center">
            <div className="w-12 h-12 rounded-xl bg-yellow-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Zap className="w-6 h-6 text-yellow-400" />
            </div>
            <h3 className="font-bold text-white mb-2">{t('academyFeature2Title')}</h3>
            <p className="text-gray-400 text-sm">{t('academyFeature2Desc')}</p>
          </div>
          <div className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl p-6 border border-zinc-800 hover:border-orange-500/40 transition-all group text-center">
            <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Flame className="w-6 h-6 text-orange-400" />
            </div>
            <h3 className="font-bold text-white mb-2">{t('academyFeature3Title')}</h3>
            <p className="text-gray-400 text-sm">{t('academyFeature3Desc')}</p>
          </div>
          <div className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl p-6 border border-zinc-800 hover:border-orange-500/40 transition-all group text-center">
            <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
              <Trophy className="w-6 h-6 text-green-400" />
            </div>
            <h3 className="font-bold text-white mb-2">{t('academyFeature4Title')}</h3>
            <p className="text-gray-400 text-sm">{t('academyFeature4Desc')}</p>
          </div>
        </div>

        {/* Big Academy Preview Card */}
        <div className="relative rounded-3xl overflow-hidden border border-orange-500/20 bg-gradient-to-br from-zinc-950 to-black">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-red-500/5 pointer-events-none" />
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-2 gap-0">
            {/* Left: Curriculum */}
            <div className="p-10 lg:border-r border-zinc-800">
              <div className="text-xs text-orange-400 font-bold uppercase tracking-widest mb-3">{t('academyModule1Label')}</div>
              <h3 className="text-2xl font-black text-white mb-6">{t('academyModule1Title')}</h3>
              <div className="space-y-3">
                {[
                  { titleKey: language === 'en' ? "What is the Stock Market?" : language === 'es' ? "¿Qué es la Bolsa?" : "C'est quoi la Bourse ?", xp: 50, done: true },
                  { titleKey: language === 'en' ? "Stocks vs Bonds" : language === 'es' ? "Acciones vs Bonos" : "Actions vs Obligations", xp: 60, done: true },
                  { titleKey: language === 'en' ? "What is an ETF?" : language === 'es' ? "¿Qué es un ETF?" : "Qu'est-ce qu'un ETF ?", xp: 60, done: false },
                  { titleKey: language === 'en' ? "Tax-Efficient Accounts" : language === 'es' ? "Cuentas fiscalmente eficientes" : "Le PEA et le Compte-Titres", xp: 70, done: false },
                  { titleKey: language === 'en' ? "Understanding Dividends" : language === 'es' ? "Entender los Dividendos" : "Comprendre les Dividendes", xp: 70, done: false },
                  { titleKey: language === 'en' ? "The Magic of Compound Interest" : language === 'es' ? "La Magia del Interés Compuesto" : "La Magie des Intérêts Composés", xp: 80, done: false },
                  { titleKey: language === 'en' ? "Your First Investment" : language === 'es' ? "Tu Primera Inversión" : "Votre Premier Investissement", xp: 100, done: false },
                ].map((lesson, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-zinc-900/50 border border-zinc-800/50">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${lesson.done ? 'bg-green-500/20' : 'bg-orange-500/10'}`}>
                      {lesson.done
                        ? <CheckCircle className="w-4 h-4 text-green-400" />
                        : <span className="text-orange-400 font-bold text-xs">{i + 1}</span>
                      }
                    </div>
                    <span className="text-gray-300 text-sm flex-1">{lesson.titleKey}</span>
                    <span className="text-xs text-orange-400 font-semibold flex items-center gap-1">
                      <Zap className="w-3 h-3" />{lesson.xp}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Level & CTA */}
            <div className="p-10 flex flex-col justify-between">
              <div>
                <div className="text-xs text-gray-500 font-bold uppercase tracking-widest mb-3">{t('academyProgressLabel')}</div>
                <div className="space-y-3 mb-8">
                  {[
                    { icon: '🌱', name: language === 'en' ? 'Beginner' : language === 'es' ? 'Principiante' : 'Débutant', xp: '0 XP', color: 'border-green-500/30 bg-green-500/5' },
                    { icon: '📚', name: language === 'en' ? 'Apprentice' : language === 'es' ? 'Aprendiz' : 'Apprenti', xp: '200 XP', color: 'border-blue-500/30 bg-blue-500/5' },
                    { icon: '💡', name: language === 'en' ? 'Initiate' : language === 'es' ? 'Iniciado' : 'Initié', xp: '500 XP', color: 'border-yellow-500/30 bg-yellow-500/5' },
                    { icon: '📈', name: language === 'en' ? 'Intermediate' : language === 'es' ? 'Intermedio' : 'Intermédiaire', xp: '1,000 XP', color: 'border-orange-500/30 bg-orange-500/5' },
                    { icon: '🏆', name: language === 'en' ? 'Expert' : language === 'es' ? 'Experto' : 'Expert', xp: '5,000 XP', color: 'border-purple-500/30 bg-purple-500/5' },
                    { icon: '👑', name: language === 'en' ? 'Legend' : language === 'es' ? 'Leyenda' : 'Légende', xp: '10,000 XP', color: 'border-red-500/30 bg-red-500/5' },
                  ].map((lvl, i) => (
                    <div key={i} className={`flex items-center gap-3 px-4 py-2 rounded-xl border ${lvl.color}`}>
                      <span className="text-lg">{lvl.icon}</span>
                      <span className="text-white font-semibold text-sm flex-1">{lvl.name}</span>
                      <span className="text-gray-400 text-xs">{lvl.xp}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  data-testid="academy-cta-btn"
                  onClick={() => { setAuthMode('register'); setShowAuth(true); }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold py-6 text-base rounded-xl"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  {t('academyCTA')}
                </Button>
                <p className="text-center text-gray-500 text-xs">{t('academyCTANote')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SIMULATEUR SECTION ============ */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="relative rounded-3xl overflow-hidden border border-zinc-800 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-10 lg:p-14">
          <div className="absolute top-0 left-0 w-72 h-72 bg-green-500/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-bold uppercase tracking-widest mb-5">
                <BarChart2 className="w-3.5 h-3.5" />
                {t('simulatorBadge')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-black text-white mb-4 leading-tight">
                {t('simulatorTitle')} <span className="text-green-400">{t('simulatorTitleBold')}</span>
              </h2>
              <p className="text-gray-400 mb-6">
                {t('simulatorDesc')}<strong className="text-white">{t('simulatorDescBold')}</strong>{t('simulatorDescEnd')}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  t('simulatorFeature1'),
                  t('simulatorFeature2'),
                  t('simulatorFeature3'),
                  t('simulatorFeature4'),
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                data-testid="simulator-cta-btn"
                onClick={() => { setAuthMode('register'); setShowAuth(true); }}
                className="bg-green-500 hover:bg-green-600 text-black font-bold px-8 py-5 rounded-xl text-sm"
              >
                <BarChart2 className="w-4 h-4 mr-2" />
                {t('simulatorCTA')}
              </Button>
            </div>

            {/* Mock Portfolio Preview */}
            <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-bold">{t('simulatorPortfolioTitle')}</span>
                <span className="text-xs text-gray-500">{t('simulatorPortfolioNote')}</span>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div className="bg-zinc-800 rounded-xl p-3 text-center">
                  <div className="text-xs text-gray-400 mb-1">{language === 'en' ? 'Value' : language === 'es' ? 'Valor' : 'Valeur'}</div>
                  <div className="text-sm font-bold text-white">$11,240</div>
                </div>
                <div className="bg-zinc-800 rounded-xl p-3 text-center">
                  <div className="text-xs text-gray-400 mb-1">Cash</div>
                  <div className="text-sm font-bold text-green-400">$4,800</div>
                </div>
                <div className="bg-zinc-800 rounded-xl p-3 text-center">
                  <div className="text-xs text-gray-400 mb-1">P&L</div>
                  <div className="text-sm font-bold text-green-400">+12.4%</div>
                </div>
              </div>

              {/* Holdings mock */}
              {[
                { symbol: 'AAPL', name: 'Apple', qty: '2', price: '$257', pnl: '+8.2%', positive: true },
                { symbol: 'BTC', name: 'Bitcoin', qty: '0.05', price: '$94k', pnl: '+21.5%', positive: true },
                { symbol: 'TSLA', name: 'Tesla', qty: '1', price: '$345', pnl: '-3.1%', positive: false },
              ].map((h, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-zinc-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                      <span className="text-orange-400 font-bold text-xs">{h.symbol.substring(0, 2)}</span>
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold">{h.symbol}</div>
                      <div className="text-gray-500 text-xs">{h.qty} × {h.price}</div>
                    </div>
                  </div>
                  <span className={`text-sm font-bold ${h.positive ? 'text-green-400' : 'text-red-400'}`}>{h.pnl}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Simple CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center">
        <h2 className="text-5xl font-bold text-white mb-6">
          {t('ctaTitle')} <span className="text-orange-500">{t('ctaTitleBold')}</span>?
        </h2>
        <p className="text-xl text-gray-400 mb-8">{t('ctaSubtitle')}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button 
            data-testid="footer-cta-button"
            onClick={() => { setAuthMode('register'); setShowAuth(true); }}
            className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-12 py-6 text-lg rounded-full"
          >
            {t('tryItNow')}
          </Button>
          <a
            href="https://play.google.com/store/apps/details?id=com.donotbull.app"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-black border border-gray-700 hover:border-orange-500 text-white font-semibold px-6 py-3.5 rounded-full transition-all group"
          >
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" xmlns="http://www.w3.org/2000/svg">
              <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-11.57L13.34 9l-10.16 14.76zm-.96-21.5C2.08 2.57 2 2.9 2 3.24v17.52c0 .34.08.67.22.96L13.06 12 2.22 2.26zM20.49 10.34l-2.76-1.57-3.67 3.36 3.67 3.36 2.8-1.59c.8-.45.8-1.11-.04-1.56zM4.17.24L16.77 11.8 13.34 15 4.17.24z" />
            </svg>
            <div className="text-left">
              <div className="text-xs text-gray-400 leading-tight">{language === 'fr' ? 'Disponible sur' : language === 'es' ? 'Disponible en' : 'Available on'}</div>
              <div className="text-sm font-bold leading-tight">Google Play</div>
            </div>
          </a>
          <a
            href="https://apps.apple.com/app/id6774533419"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 bg-black border border-gray-700 hover:border-orange-500 text-white font-semibold px-6 py-3.5 rounded-full transition-all group"
          >
            <svg viewBox="0 0 814 1000" className="w-6 h-6 fill-current text-white" xmlns="http://www.w3.org/2000/svg">
              <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 405.8 0 263.6 0 127.3c0-150.1 97.8-248.4 250.2-248.4 69 0 127.4 36.2 179 81.9 13.4 11.5 27.5 23.4 42.5 35.6 44.6-37.3 100.3-108.7 179.5-108.7 3.2 0 6.4.3 9.6.5 2.2.1 4.4.3 6.5.4l.2.1z"/>
            </svg>
            <div className="text-left">
              <div className="text-xs text-gray-400 leading-tight">{language === 'fr' ? 'Disponible sur' : language === 'es' ? 'Disponible en' : 'Available on'}</div>
              <div className="text-sm font-bold leading-tight">App Store</div>
            </div>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-12 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="DO NOT BULL" className="w-8 h-8" />
                <span className="text-lg font-bold text-white">DO NOT BULL</span>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                {t('footerCompanyInfo')}
              </p>
              <div className="border-t border-zinc-800 pt-3 mt-3">
                <p className="text-xs text-gray-500 leading-relaxed">
                  <span className="text-gray-400 font-semibold">Do Not Bull</span> {language === 'fr' ? 'est une marque de' : language === 'es' ? 'es una marca de' : 'is a brand of'}<br />
                  <span className="text-orange-500 font-semibold">Strategy Intelligence Solutions LTD</span><br />
                  Registered in England & Wales<br />
                  Company No. 16760736<br />
                  128 City Road, London EC1V 2NX
                </p>
              </div>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footerLegal')}</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('termsConditions')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('privacyPolicy')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/disclaimer')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('legalDisclaimer')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footerPolicies')}</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/refund-policy')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('refundPolicy')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/subscription-policy')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('subscriptionPolicy')}
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/cookie-policy')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('cookiePolicy')}
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footerContact')}</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-orange-500 text-sm">
                    {t('contactUs')}
                  </button>
                </li>
                <li>
                  <a href="mailto:support@donotbull.com" className="text-gray-400 hover:text-orange-500 text-sm">
                    support@donotbull.com
                  </a>
                </li>
                <li>
                  <a href="mailto:contact@donotbull.com" className="text-gray-400 hover:text-orange-500 text-sm">
                    contact@donotbull.com
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} Do Not Bull — Strategy Intelligence Solutions LTD. {t('footerCopyright').replace('DoNotBull.', '')}
              </p>
              <div className="flex items-center gap-2">
                <a
                  href="https://play.google.com/store/apps/details?id=com.donotbull.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-black border border-gray-700 hover:border-orange-500 text-white text-xs font-semibold px-4 py-2 rounded-full transition-all"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current text-green-400" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.18 23.76c.3.17.64.24.99.2l12.6-11.57L13.34 9l-10.16 14.76zm-.96-21.5C2.08 2.57 2 2.9 2 3.24v17.52c0 .34.08.67.22.96L13.06 12 2.22 2.26zM20.49 10.34l-2.76-1.57-3.67 3.36 3.67 3.36 2.8-1.59c.8-.45.8-1.01-.04-1.56zM4.17.24L16.77 11.8 13.34 15 4.17.24z" />
                  </svg>
                  Google Play
                </a>
                <a
                  href="https://apps.apple.com/app/id6774533419"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-black border border-gray-700 hover:border-orange-500 text-white text-xs font-semibold px-4 py-2 rounded-full transition-all"
                >
                  <svg viewBox="0 0 814 1000" className="w-4 h-4 fill-current text-white" xmlns="http://www.w3.org/2000/svg">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 405.8 0 263.6 0 127.3c0-150.1 97.8-248.4 250.2-248.4 69 0 127.4 36.2 179 81.9 13.4 11.5 27.5 23.4 42.5 35.6 44.6-37.3 100.3-108.7 179.5-108.7 3.2 0 6.4.3 9.6.5 2.2.1 4.4.3 6.5.4l.2.1z"/>
                  </svg>
                  App Store
                </a>
              </div>
              <p className="text-gray-600 text-xs text-center md:text-right max-w-lg">
                {t('footerDisclaimer')}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent data-testid="auth-dialog" className="bg-[#0a0a0a] border-orange-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <img src="/logo.png" alt="DO NOT BULL" className="w-6 h-6" />
              <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">DO NOT BULL</span>
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={authMode} onValueChange={setAuthMode}>
            <TabsList className="grid w-full grid-cols-2 bg-zinc-900">
              <TabsTrigger data-testid="login-tab" value="login">{t('login')}</TabsTrigger>
              <TabsTrigger data-testid="register-tab" value="register">{t('signUp')}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form data-testid="login-form" onSubmit={handleAuth} className="space-y-4 mt-4">
                <div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                    <Input
                      data-testid="login-email-input"
                      type="email"
                      placeholder={t('email')}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-12 bg-zinc-900 border-zinc-700 text-white focus:border-orange-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                    <Input
                      data-testid="login-password-input"
                      type="password"
                      placeholder={t('password')}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-12 bg-zinc-900 border-zinc-700 text-white focus:border-orange-500"
                      required
                    />
                  </div>
                </div>
                <Button data-testid="login-submit-button" type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
                  {t('login')}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="register">
              <form data-testid="register-form" onSubmit={handleAuth} className="space-y-4 mt-4">
                <div>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                    <Input
                      data-testid="register-name-input"
                      type="text"
                      placeholder={t('fullName')}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-12 bg-zinc-900 border-zinc-700 text-white focus:border-orange-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                    <Input
                      data-testid="register-email-input"
                      type="email"
                      placeholder={t('email')}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-12 bg-zinc-900 border-zinc-700 text-white focus:border-orange-500"
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                    <Input
                      data-testid="register-password-input"
                      type="password"
                      placeholder={t('password')}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-12 bg-zinc-900 border-zinc-700 text-white focus:border-orange-500"
                      required
                    />
                  </div>
                </div>

                {/* VIP Code field */}
                <div>
                  <div className="relative">
                    <Shield className="absolute left-3 top-3 w-5 h-5 text-gray-500" />
                    <Input
                      data-testid="register-vip-input"
                      type="text"
                      placeholder={language === 'fr' ? 'Code VIP (optionnel)' : language === 'es' ? 'Código VIP (opcional)' : 'VIP Code (optional)'}
                      value={formData.vip_code || ''}
                      onChange={(e) => setFormData({ ...formData, vip_code: e.target.value.toUpperCase() })}
                      className="pl-12 bg-zinc-900 border-zinc-700 text-white focus:border-orange-500 font-mono tracking-widest"
                    />
                  </div>
                  {formData.vip_code?.length > 3 && (
                    <VipCodeValidator code={formData.vip_code} language={language} />
                  )}
                </div>

                <Button data-testid="register-submit-button" type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
                  {t('signUp')}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default LandingPage;
